import type { AssociationType } from '@/lib/crm/core/SchemaClient';

import { getMemoAssociationNames, type MemoEntityType } from './notes.utils';

/**
 * Target entity types that have no meaningful "link a note to this record"
 * semantics and are excluded from the link-type picker AND the existing-
 * links display in both NoteCreateModal and NoteLinkedRecordsSection.
 *
 * - ahoy_import: internal source-import artifact, not a CRM link target
 * - ahoy_note_pipeline / ahoy_task_pipeline: notes and tasks link to a
 *   STAGE, not the pipeline itself
 * - ahoy_note_pipeline_stage / ahoy_task_pipeline_stage: a note/task links
 *   to a STAGE only through the Status row, never as a user-picked linked
 *   record — belt-and-braces so a stage is never a link target on any
 *   surface. ahoy_note_pipeline_stage is retired (notes-as-tasks epic) but
 *   still excluded defensively alongside its task counterpart.
 * - ahoy_file: files have their own attach surface
 * - ahoy_team: teams are not a CRM link target
 * - ahoy_stage_timer: internal SLA/dwell-time tracking record stamped onto
 *   notes when they enter a stage; auto-created and not user-actionable,
 *   so it has no business showing up in the slideout's "Linked records".
 * - ahoy_external_integration: the source integration a note was imported
 *   from ("Source Integration"). System provenance, not a CRM record a user
 *   would deliberately link a note to — unlike its `ahoy_import` sibling it
 *   is write:all, so it isn't caught by the internal-write filter below and
 *   must be excluded here.
 */
export const EXCLUDED_LINK_TARGETS = new Set<string>([
  'ahoy_import',
  'ahoy_note_pipeline',
  'ahoy_task_pipeline',
  'ahoy_note_pipeline_stage',
  'ahoy_task_pipeline_stage',
  'ahoy_file',
  'ahoy_team',
  'ahoy_stage_timer',
  'ahoy_external_integration',
  // Automation plumbing: audit records, webhook endpoints and the secrets
  // that authenticate them. Every principal-kind type carries edges to these
  // (the audit one twice — once via the `any` kind, once via `automation`),
  // so without this they flood an automation's add-column picker and link
  // picker with infra nobody would link a note to or read as a column.
  'ahoy_audit',
  'ahoy_secret',
  'ahoy_automation_webhook',
  'ahoy_automation_webhook_secret',
  'ahoy_outbound_webhook',
]);

/**
 * Removes association types whose target entity type is in
 * EXCLUDED_LINK_TARGETS, names a `*_timeline` identifier, or has
 * `value_write_access: 'internal'`. Timeline identifiers are
 * timeline-event type names (e.g. `ahoy_meeting_timeline`), not entity
 * types — exposing them as linkable targets would surface invalid link
 * options that can't be persisted as real associations. Internal-write
 * types are reserved for backend/system actors (stamped automatically);
 * surfacing them in the picker would let users pick a link type the
 * server will reject with a permission error. Does not mutate the input
 * array.
 */
export function filterLinkAssociationTypes(
  types: AssociationType[]
): AssociationType[] {
  return types.filter(
    at =>
      !EXCLUDED_LINK_TARGETS.has(at.target_entity_type_name) &&
      !at.target_entity_type_name.endsWith('_timeline') &&
      at.value_write_access !== 'internal'
  );
}

/**
 * Build the `{ id, label }` items for the link-type Select from a list of
 * linkable association types. Drops the leading "Associated " from labels
 * (e.g. "Associated Contacts" → "Contacts") so the picker reads cleanly.
 *
 * Shared by the composer (`useMemoCreate`) and the slideout
 * (`NoteLinkedRecordsSection`) so their pickers render identical labels.
 */
export function toLinkPickerItems(
  types: AssociationType[]
): { id: string; label: string }[] {
  return types.map(at => ({
    id: at.association_type_name,
    label: (at.label || at.association_type_name).replace(
      /^Associated\s+/i,
      ''
    ),
  }));
}

/**
 * Outbound associations the slideout surfaces elsewhere (owner / stage /
 * creator / visibility / mentions) — hidden from the generic "Linked
 * records" list so they don't appear twice. Mirrors the exclusion set in
 * NoteCreateModal so the create flow and the slideout agree on what
 * counts as a "linked record" vs. a form-managed link.
 *
 * Built per note-like entity type: a task's owner / creator / stage /
 * visibility edges are the note's renamed (`ahoy_task_to_owner`, …), and the
 * task slideout must hide exactly the set the note slideout hides. Both sets
 * are module-level so their identity is stable across renders.
 *
 * Combined with `filterLinkAssociationTypes` (which drops noise target
 * types like ahoy_import / ahoy_note_pipeline / ahoy_file / ahoy_team),
 * this set defines the unified "what is a user-visible linked record"
 * rule used by BOTH the picker (what types you can add) and the display
 * loop (what types are shown for existing links).
 */
export function buildHiddenAssociationTypes(
  entityType: MemoEntityType
): ReadonlySet<string> {
  const names = getMemoAssociationNames(entityType);
  return new Set<string>(
    [
      names.creator,
      names.visibleOrg,
      names.visibleUser,
      names.mentionedUser,
      // Owner — rendered as the avatar picker on NoteCard / the Assignee row.
      names.owner,
      // Stage — surfaced via the status picker in the form / the Status row.
      // null for the note family, which has no stage concept.
      names.stage,
      // Reminders — system-managed by the reminder automation, not user-facing.
      'ahoy_subject_to_reminder',
    ].filter((name): name is string => name !== null)
  );
}

export const HIDDEN_ASSOCIATION_TYPES_BY_ENTITY_TYPE: Record<
  MemoEntityType,
  ReadonlySet<string>
> = {
  ahoy_note: buildHiddenAssociationTypes('ahoy_note'),
  ahoy_task: buildHiddenAssociationTypes('ahoy_task'),
};
